import { DAEMON_URL } from "../config";
import { HttpError } from "./errors";

export type AdminUser = {
  id: number;
  email: string;
  role: "admin" | "member";
  created_at: string;
  last_login_at: string | null;
  totp_verified: number;
  daily_usd_cents: number | null;
  monthly_usd_cents: number | null;
};

/**
 * Partial PATCH: pass only the field that changed. Values are USD dollars
 * (e.g. 5 for $5.00). Backend stores as integer cents.
 */
export async function setUserLimits(
  userId: number,
  patch: { dailyUsd?: number | null; monthlyUsd?: number | null },
): Promise<{ dailyCents: number | null; monthlyCents: number | null }> {
  return req<{ ok: boolean; dailyCents: number | null; monthlyCents: number | null }>(
    `/admin/users/${userId}/limits`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(patch),
    },
  );
}

/**
 * A newly created account plus the one-time link that activates it.
 *
 * There is no `tempPassword`: the admin never learns a password they would
 * then have to relay. The account is minted with an unguessable random one
 * nobody holds, and `inviteUrl` is the only way in until it expires.
 */
export type CreatedUser = {
  id: number;
  email: string;
  role: "admin" | "member";
  inviteUrl: string;
  /** ISO timestamp after which the link stops working. */
  expiresAt: string;
};

/** A freshly minted one-time link, from either invite or password reset. */
export type InviteLink = {
  inviteUrl: string;
  expiresAt: string;
};

async function req<T>(path: string, init: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}${path}`, { ...init, credentials: "include" });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  const text = await res.text();
  let data: unknown;
  try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
  if (!res.ok) {
    const msg = (data as { error?: string })?.error ?? `http ${res.status}`;
    throw new HttpError(res.status, msg);
  }
  return data as T;
}

export async function listUsers(): Promise<AdminUser[]> {
  const j = await req<{ users: AdminUser[] }>("/admin/users", {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.users;
}

export async function createUser(email: string, role: "admin" | "member"): Promise<CreatedUser> {
  return req<CreatedUser>("/admin/users", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, role }),
  });
}

/**
 * Revoke any outstanding invite for this account and mint a fresh link.
 *
 * Doubles as the password reset: redeeming the link sets a new password. An
 * established account keeps its authenticator, so this hands back the password
 * without handing back the second factor.
 */
export async function reissueInvite(id: number): Promise<InviteLink> {
  return req<InviteLink>(`/admin/users/${id}/invite`, {
    method: "POST",
    headers: { Accept: "application/json" },
  });
}

export async function deleteUser(id: number): Promise<void> {
  await req<{ ok: boolean }>(`/admin/users/${id}`, { method: "DELETE" });
}

export async function setUserRole(id: number, role: "admin" | "member"): Promise<void> {
  await req<{ ok: boolean }>(`/admin/users/${id}/role`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ role }),
  });
}

export type AdminProject = {
  id: string;
  name: string;
  kind: "addon" | "tool" | "meta";
  assignedUserCount: number;
  /** Configured vault paths; `unusable` lists the ones that give the AI nothing. */
  obsidian?: {
    paths: string[];
    vaultConfigured: boolean;
    unusable: { path: string; kind: "missing" | "outside-vault" | "not-markdown" }[];
  };
};

export async function listAllProjects(): Promise<AdminProject[]> {
  const j = await req<{ projects: AdminProject[] }>("/admin/projects", {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.projects;
}

export async function getUserProjects(userId: number): Promise<string[]> {
  const j = await req<{ projectIds: string[] }>(`/admin/users/${userId}/projects`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.projectIds;
}

export type UsageBucket = { cents: number };
export type UserUsage = { d1: UsageBucket; d7: UsageBucket; d30: UsageBucket };
export type UsageByEmail = Record<string, UserUsage>;

export async function listUserUsage(): Promise<UsageByEmail> {
  const j = await req<{ usage: UsageByEmail }>("/admin/users/usage", {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.usage;
}

export type UsageTokenSplit = { claude: number; codex: number; total: number };
export type UsageNullableSplit = { claude: number | null; codex: number | null; total: number | null };
export type UsageActivitySplit = { claude: string | null; codex: string | null; total: string | null };
export type UsagePlanTool = "claude" | "codex";
export type UsagePlanSetting = {
  tool: UsagePlanTool;
  planCount: number;
  weeklyPercentages: Array<number | null>;
  manualPercentages: Array<number | null>;
  /** Stable alias of the source each slot is bound to, null while unbound. */
  sourceIds?: Array<string | null>;
  plans: Array<{
    percent: number | null;
    source: "automatic" | "manual" | "unavailable";
    observedAt: string | null;
    resetAt: string | null;
    stale: boolean;
    /** Why this slot has no current automatic figure. Already redacted. */
    detail?: string | null;
    /** This slot's source resolved to an account an earlier slot holds. */
    duplicate?: boolean;
  }>;
  averagePercent: number | null;
  reportedPlans: number;
  automaticPlans: number;
  manualPlans: number;
  duplicatePlans?: number;
  /** Readings collected but shown by no slot, i.e. more sources than plans. */
  unmappedReadings?: number;
  updatedAt: string | null;
  refreshedAt: string | null;
};

export type UsageTimelineGranularity = "week" | "month";

export type UsageBucketProject = {
  id: string;
  tokens: UsageTokenSplit;
  costUsd: UsageTokenSplit;
  sharePercent: number;
};

export type UsageTimelineBucket = {
  start: string;
  end: string;
  label: string;
  tokens: UsageTokenSplit;
  costUsd: UsageTokenSplit;
  costPerMillionTokensUsd: UsageNullableSplit;
  /** Where this bucket's money went. Absent on a daemon that predates the breakdown. */
  projects?: UsageBucketProject[];
  /** Fee owed for a tool nobody measurably used in this bucket. */
  unattributedCostUsd?: UsageTokenSplit;
  days: number;
  partial: boolean;
  current: boolean;
};

export type UsageTimeline = {
  granularity: UsageTimelineGranularity;
  label: string;
  buckets: UsageTimelineBucket[];
  totals: {
    tokens: UsageTokenSplit;
    costUsd: UsageTokenSplit;
    costPerMillionTokensUsd: UsageNullableSplit;
    days: number;
  };
  coverageStart: string | null;
  observedDays: number;
};

export type AiUsageReport = {
  generatedAt: string;
  coverageStart: string | null;
  methodology: "native_cli_token_activity";
  summary: {
    monthlyFixedCostUsd: number;
    currentWeek: UsageTokenSplit;
    previousWeek: UsageTokenSplit;
    currentWeekPace: UsageTokenSplit;
    weeklyTrend: UsageNullableSplit;
    currentWeekPaceTokens: number;
    weeklyTrendPercent: number | null;
  };
  benchmark: {
    days: 30;
    tokens: UsageTokenSplit;
    previousTokens: UsageTokenSplit;
    dailyAverageTokens: number;
    changePercent: number | null;
  };
  forecast: {
    month: string;
    days: number;
    observedDays: number;
    observedDaysByTool?: UsageTokenSplit;
    tokens: UsageTokenSplit;
    confidence: "low" | "medium" | "high";
    confidenceByTool?: { claude: "low" | "medium" | "high"; codex: "low" | "medium" | "high"; total: "low" | "medium" | "high" };
  };
  subscriptions: Array<{
    id: "claude-code" | "chatgpt";
    provider: "Anthropic" | "OpenAI";
    product: string;
    tool: "Claude Code" | "Codex";
    seats: number;
    monthlyCostUsd: number;
    connected: boolean;
    complete: boolean;
    connectedSources: number;
    filesDiscovered: number;
    filesScanned: number;
    currentWeekTokens: number;
    last30DaysTokens: number;
    projectedNextMonthTokens: number;
    lastActivityAt: string | null;
    weeklyUtilization?: UsagePlanSetting;
  }>;
  weeks: Array<{ start: string; end: string; claude: number; codex: number; total: number; current: boolean }>;
  /** Absent on a daemon that predates the expense rollup, or when its store is unreadable. */
  timeline?: Partial<Record<UsageTimelineGranularity, UsageTimeline>>;
  projects: Array<{
    id: string;
    name: string;
    registered: boolean;
    groupType: "workspace_project" | "ai_agent" | "local";
    currentWeek: UsageTokenSplit;
    previousWeek: UsageTokenSplit;
    last30Days: UsageTokenSplit;
    projectedNextMonth: UsageTokenSplit;
    weeklyPace: UsageTokenSplit;
    weeklyTrend: UsageNullableSplit;
    weeklyPaceTokens: number;
    weeklyTrendPercent: number | null;
    currentWeekSessions: number;
    currentWeekSessionsByTool: UsageTokenSplit;
    last30DaysSessionsByTool: UsageTokenSplit;
    sharePercent: number;
    sharePercentByTool?: UsageTokenSplit;
    estimatedMonthlyAllocationUsd: number;
    estimatedMonthlyAllocationUsdByTool?: UsageTokenSplit;
    lastActivityAt: string | null;
    lastActivityAtByTool: UsageActivitySplit;
  }>;
};

export async function getAiUsage(): Promise<AiUsageReport> {
  const j = await req<{ report: AiUsageReport }>("/admin/usage", {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.report;
}

/**
 * One assistant turn that failed or recorded a reason.
 *
 * Hand-mirrored from `TurnErrorRow` in `src/db/turn-errors.ts`. Nothing enforces
 * that the two stay in step, so a field renamed there has to be renamed here.
 *
 * `status` is carried rather than folded into a boolean because an
 * `empty_completion` rides on a turn whose status is `complete`: without it the
 * page would present a success as a failure. `durationMs` is null when the turn
 * died before its first checkpoint, which is unknown rather than zero.
 */
export type TurnErrorRow = {
  id: number;
  conversationId: number;
  createdAt: number;
  durationMs: number | null;
  user: string;
  project: string;
  code: string | null;
  text: string | null;
  tokens: number | null;
  status: string;
};

/**
 * How many failed turns the page asks for.
 *
 * The server applies this same default when the parameter is absent, and its
 * envelope carries no total, so a caller that stays silent cannot tell a
 * complete answer from a capped one: 200 rows back means either 200 failures or
 * the newest 200 of far more. Naming the number here lets the page compare
 * against it and say which of the two it is showing.
 */
export const TURN_ERRORS_LIMIT = 200;

export async function getTurnErrors(limit: number = TURN_ERRORS_LIMIT): Promise<TurnErrorRow[]> {
  const j = await req<{ errors: TurnErrorRow[] }>(`/admin/errors?limit=${limit}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.errors;
}

export async function setAiUsagePlans(tool: UsagePlanTool, weeklyPercentages: Array<number | null>): Promise<UsagePlanSetting> {
  const j = await req<{ setting: UsagePlanSetting }>("/admin/usage/plans", {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ tool, weeklyPercentages }),
  });
  return j.setting;
}

export async function setUserProjects(userId: number, projectIds: string[]): Promise<string[]> {
  const j = await req<{ projectIds: string[] }>(`/admin/users/${userId}/projects`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ projectIds }),
  });
  return j.projectIds;
}
